// Self-test registry: quick, READ-ONLY checks for every menu item.
// To add a check: append to the menu's `checks` array. Each check gets a helper `ctx`:
//   ctx.get(path)  -> { status, ok, data, ms }   (authenticated GET against /api)
//   ctx.can(priv)  -> boolean
//   ctx.shared     -> object for passing values between checks (e.g. a model id)
// Return { status: 'pass'|'fail'|'warn'|'skip', detail } or throw (counts as fail).
// Never add checks that create, change or delete data here.

const isArr = (x) => Array.isArray(x);
const listOf = (d, ...keys) => isArr(d) ? d : (keys.map(k => d && d[k]).find(isArr) || null);

// Shorthand: GET path, require 2xx, optionally validate the body.
const apiCheck = (name, path, { priv, validate } = {}) => ({
  name,
  run: async (ctx) => {
    if (priv && !ctx.can(priv)) return { status: 'skip', detail: `needs '${priv}' privilege` };
    const p = typeof path === 'function' ? path(ctx) : path;
    if (!p) return { status: 'skip', detail: 'prerequisite missing' };
    const r = await ctx.get(p);
    if (!r.ok) return { status: 'fail', detail: `GET ${p} → HTTP ${r.status}${r.data && r.data.error ? ': ' + r.data.error : ''}` };
    if (r.data && r.data.error) return { status: 'fail', detail: `GET ${p} → ${r.data.error}` };
    if (validate) {
      const v = validate(r.data, ctx);
      if (v) return typeof v === 'string' ? { status: 'fail', detail: v } : v;
    }
    return { status: 'pass', detail: `GET ${p} · ${r.ms} ms` };
  },
});

export const SELF_TESTS = [
  {
    nav: 'core', label: 'Login & session',
    checks: [
      apiCheck('Session token is valid', '/me', {
        validate: (d, ctx) => { if (!d.username) return 'no username returned'; ctx.shared.me = d; },
      }),
    ],
  },
  {
    nav: 'eval', label: 'Global Model Eval',
    checks: [
      apiCheck('Catalog loads', '/catalog', {
        validate: (d, ctx) => {
          const l = listOf(d, 'items');
          if (!l) return 'response is not a list';
          ctx.shared.catalog = l;
          const appr = l.filter(i => i.compliance_status === 'ECHOSTAR_APPROVED').length;
          if (l.length === 0) return { status: 'warn', detail: 'catalog is empty' };
          ctx.shared.modelId = (l[0] || {}).model_id;
          return { status: 'pass', detail: `${l.length} models (${appr} EchoStar, ${l.length - appr} Other)` };
        },
      }),
      apiCheck('Scorecard for a model', (ctx) => ctx.shared.modelId && `/scorecard/${encodeURIComponent(ctx.shared.modelId)}`),
      apiCheck('Test suites load', '/test-suite', {
        validate: (d) => {
          const l = listOf(d, 'items', 'suites');
          if (l && l.length === 0) return { status: 'warn', detail: 'no test-suite prompts (Compare and Evaluate will be limited)' };
        },
      }),
      {
        name: 'Approved models have a valid VM',
        run: async (ctx) => {
          const l = ctx.shared.catalog;
          if (!l) return { status: 'skip', detail: 'catalog not loaded' };
          const bad = l.filter(i => i.compliance_status === 'ECHOSTAR_APPROVED' &&
            !/^echostar\/(fast|smart|balanced|advanced)-v\d+$/.test(i.assigned_virtual_model || ''));
          return bad.length
            ? { status: 'warn', detail: `${bad.length} approved model(s) with missing/unknown VM: ${bad.slice(0, 3).map(b => b.model_id).join(', ')}` }
            : { status: 'pass', detail: 'all approved models map to a known VM' };
        },
      },
      {
        name: 'Flip weights add up to 100%',
        run: async (ctx) => {
          const l = ctx.shared.catalog;
          if (!l) return { status: 'skip', detail: 'catalog not loaded' };
          const sums = {};
          l.forEach(i => Object.entries(i.vm_placements || {}).forEach(([env, p]) => {
            if (p && p.weight !== undefined && p.weight !== null && !p.tier) {
              const k = `${p.vm}::${env}`; sums[k] = (sums[k] || 0) + Number(p.weight);
            }
          }));
          const off = Object.entries(sums).filter(([, s]) => Math.abs(s - 100) > 1);
          if (!Object.keys(sums).length) return { status: 'skip', detail: 'no weighted placements yet' };
          return off.length
            ? { status: 'warn', detail: 'saved weights drift from 100%: ' + off.map(([k, s]) => `${k}=${s}%`).join(', ') + ' (models added in TrueFoundry directly are not tracked)' }
            : { status: 'pass', detail: `${Object.keys(sums).length} VM/env pools sum to 100%` };
        },
      },
      apiCheck('TrueFoundry connection', '/truefoundry-test', {
        priv: 'manage_config',
        validate: (d) => (d.ok === false || d.status === 'NOT_CONFIGURED')
          ? { status: 'warn', detail: d.message || d.status || 'TrueFoundry not reachable / not configured' } : null,
      }),
      apiCheck('Backend logs readable', '/logs?component=backend'),
    ],
  },
  { nav: 'benchmarking', label: 'Benchmarking Platform', checks: [] },
  {
    nav: 'tsa', label: 'TSA Persona',
    checks: [
      apiCheck('Persona models load', '/persona-models', { priv: 'use_persona' }),
      apiCheck('Personas load', '/personas', { priv: 'use_persona' }),
    ],
  },
  {
    nav: 'vending', label: 'Lab Vending',
    checks: [
      apiCheck('Blueprints load', '/lab-blueprints', {
        priv: 'use_lab_vending',
        validate: (d) => { const l = listOf(d, 'blueprints', 'items'); if (l && !l.length) return { status: 'warn', detail: 'no blueprints' }; },
      }),
      apiCheck('Vends load', '/lab-vends', { priv: 'use_lab_vending' }),
      apiCheck('Costs load', '/lab-costs?days=7', { priv: 'use_lab_vending' }),
      apiCheck('TrueFoundry model list', '/lab-tf-models', { priv: 'use_lab_vending' }),
    ],
  },
  { nav: 'intake', label: 'AI Intake', checks: [] },
  {
    nav: 'admin', label: 'Admin',
    checks: [
      apiCheck('Config loads', '/config', { priv: 'manage_config' }),
      apiCheck('Users load', '/users', { priv: 'manage_users' }),
      apiCheck('GPU worker status', '/gpu-worker', { priv: 'manage_config' }),
    ],
  },
];
